// Hand-maintained GraphQL operation strings. Phase 1 has 5 ops total;
// not worth a codegen step. If we add more than ~10, switch to
// graphql-codegen and generate this file.

export const SIGNIN_MUTATION = /* GraphQL */ `
  mutation Signin($email: String!, $password: String!) {
    signin(email: $email, password: $password) {
      status
      errorCode
      message
      jwt
      passwordChangeRequired
      user {
        email
        role
        assignedTechId
      }
    }
  }
`;

export const CHANGE_PASSWORD_MUTATION = /* GraphQL */ `
  mutation ChangePassword($oldPassword: String!, $newPassword: String!) {
    changePassword(oldPassword: $oldPassword, newPassword: $newPassword) {
      status
      errorCode
      message
      jwt
    }
  }
`;

export const LIST_APPLICATIONS_QUERY = /* GraphQL */ `
  query ListApplications(
    $status: ApplicationStatus
    $limit: Int
    $nextToken: String
  ) {
    listApplications(status: $status, limit: $limit, nextToken: $nextToken) {
      applications {
        applicationID
        status
        applicationPath
        merchantEmail
        businessName
        dbaName
        cardConnectMerchantID
        frontEndMid
        pdiOrderStatus
        intakeRecipientType
        intakeProgressPct
        intakeOpensCount
        autoSubmit
        assignedTechEmail
        assignedToPIN
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_APPLICATION_QUERY = /* GraphQL */ `
  query GetApplication($applicationID: ID!) {
    getApplication(applicationID: $applicationID) {
      applicationID
      status
      applicationPath
      merchantEmail
      businessName
      templateID
      salesCode
      cardConnectMerchantID
      signatureUrl
      frontEndMid
      pollAttempts
      nextPollAfter
      errorDetails
      needsIdentityReentry
      pdiOrderStatus
      pdiOrderSentAt
      pdiOrderError
      pdiOrderDryRun
      pdiPrefs {
        sendPdiOrder
        posPlatform
        singleOrMultiSite
        gasPumpYn
        gasPumpType
        broadbandYn
        managedWifiInterest
        residentialLocation
        shippingSpeed
        notes
      }
      assignedTechEmail
      assignedToPIN
      intakeRecipientType
      intakeRecipientEmail
      intakeCcEmail
      intakeTokenExpiresAt
      intakeFirstOpenedAt
      intakeLastOpenedAt
      intakeOpensCount
      intakeProgressPct
      intakeRevoked
      intakeSubmittedAt
      autoSubmit
      mnspEnrollmentRequested
      terminalRentalRequested
      mnspEnrollmentSubmittedAt
      mnspEnrollmentPdfKey
      mnspEnrollment {
        serviceType
        registerOther
        terminalMonthlyRentalFee
        deploymentFeeOverride
        monthlyFeeOverride
        gatewayName
        terminalType
        pinPad
        internetGatewayProvided
        shippingSpeed
        shippingFeeOverride
        shippingFeeOverrideSpeed
        signatureImage
        acceptances {
          section
          acceptedAt
          acceptedBy
        }
        signatureMeta {
          ip
          userAgent
          signedAt
          lastPatchIp
        }
        pdfSha256
      }
      averageMonthlyVolume
      highTicketAmount
      averageTicketAmount
      createdAt
      updatedAt
      preArchiveStatus
      storeType
      dbaName
      taxFilingName
      taxFilingMethod
      businessDescription
      website
      businessStartDate
      contactPhone
      mcc
      businessAddress {
        address1
        address2
        city
        stateCd
        zip
        countryCd
      }
      mailingAddress {
        address1
        address2
        city
        stateCd
        zip
        countryCd
      }
      owner {
        firstName
        lastName
        title
        email
        phone
        ownerMobilePhone
        ownershipTypeCd
        ownershipPct
        dateOfBirth
        driversLicenseStateCd
        address {
          address1
          address2
          city
          stateCd
          zip
          countryCd
        }
      }
      bankDetail {
        deposit {
          accountType
          bankName
        }
        withdrawal {
          accountType
          bankName
        }
      }
      persistedSensitiveLast4 {
        taxId
        ownerSsn
        ownerDriversLicenseNumber
        depositAccountNumber
        depositRoutingNumber
        withdrawalAccountNumber
        withdrawalRoutingNumber
        source
      }
      modeOfTransaction {
        eCommercePct
        keyedPct
        mailOrderPct
        swipedPct
      }
      authFeeOverride
      feeOverrides
      icPlusPricingOverrides
      cloverSecurityAndTransarmor
      fleetOverrides
      annualSecurityBundleFee
      internalNotes
    }
  }
`;

export const GET_APPLICATION_AUDIT_LOG_QUERY = /* GraphQL */ `
  query GetApplicationAuditLog($applicationID: ID!) {
    getApplicationAuditLog(applicationID: $applicationID) {
      logs {
        eventID
        timestamp
        eventType
        applicationID
        actor
        detail
      }
      nextToken
    }
  }
`;

export const CREATE_APPLICATION_MUTATION = /* GraphQL */ `
  mutation CreateApplication($input: CreateApplicationInput!) {
    createApplication(input: $input) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        applicationPath
        businessName
        merchantEmail
        cardConnectMerchantID
        signatureUrl
        errorDetails
        createdAt
        updatedAt
      }
    }
  }
`;

// Operator action: save the Create-Application form as an in-progress draft.
// Same input as createApplication, but the backend neither submits to
// CardConnect nor sends an intake link — the row is persisted at CREATED
// (or READY_FOR_REVIEW when already complete) for the operator to finish
// later from the detail page.
//
// Optional `intake` block: when the operator was on the "email an intake
// link" path, it stages the chosen recipient / auto-submit / personal
// message on the row WITHOUT generating a token or sending an email. The
// detail page's "Send intake link" action (resendIntakeLink's no-args path)
// then emails exactly that setup when the operator is ready — and when the
// staged draft is already complete it lands at READY_FOR_REVIEW too, so it
// can alternatively be submitted straight to CardConnect from there.
//
// Response fragment mirrors CREATE_APPLICATION_MUTATION so NewApplication
// can reuse the same navigate-on-applicationID flow.
export const SAVE_DRAFT_APPLICATION_MUTATION = /* GraphQL */ `
  mutation SaveDraftApplication(
    $input: CreateApplicationInput!
    $intake: SendIntakeLinkInput
  ) {
    saveDraftApplication(application: $input, intake: $intake) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        applicationPath
        businessName
        merchantEmail
        cardConnectMerchantID
        signatureUrl
        errorDetails
        intakeRecipientType
        intakeRecipientEmail
        intakeCcEmail
        autoSubmit
        createdAt
        updatedAt
      }
    }
  }
`;

export const RESEND_SIGNATURE_MUTATION = /* GraphQL */ `
  mutation ResendSignature($applicationID: ID!) {
    resendSignature(applicationID: $applicationID) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        signatureUrl
        updatedAt
      }
    }
  }
`;

// Operator action: edit the fields NewApplication's create form collects
// that persist onto the row (see UpdateApplicationInput in lib/types.ts
// for exactly which fields, and why owner/bank/tax data isn't among
// them). Response fragment mirrors the other action mutations -- just
// enough to confirm success; ApplicationDetail refetches the full row
// via GET_APPLICATION_QUERY afterward.
export const UPDATE_APPLICATION_MUTATION = /* GraphQL */ `
  mutation UpdateApplication(
    $applicationID: ID!
    $input: UpdateApplicationInput!
    $cardConnectAction: CardConnectEditAction
  ) {
    updateApplication(
      applicationID: $applicationID
      input: $input
      cardConnectAction: $cardConnectAction
    ) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        businessName
        merchantEmail
        updatedAt
        # EMPLOYEE-only last-4 confirmation of the staged sensitive leaves
        # (#130). Null for a TECH caller. The modal refetches getApplication
        # after save; this is here so the mutation round-trip stays in parity.
        persistedSensitiveLast4 {
          taxId
          ownerSsn
          ownerDriversLicenseNumber
          depositAccountNumber
          depositRoutingNumber
          withdrawalAccountNumber
          withdrawalRoutingNumber
          source
        }
      }
    }
  }
`;

// Operator action: manually flip the status column to ARCHIVED, stamping
// the row's current status into preArchiveStatus so it can be restored
// later. Calls updateApplicationStatus directly (rather than through a
// dedicated ApplicationResult-returning mutation) — passing
// preArchiveStatus is also what tells the resolver's audit-log pipeline
// function this is an archive action (see updateApplicationStatusAuditLog.js),
// so an "application_archived" row gets written. EMPLOYEE-only; a TECH
// caller gets Unauthorized back as a GraphQL error.
export const ARCHIVE_APPLICATION_MUTATION = /* GraphQL */ `
  mutation ArchiveApplication(
    $applicationID: ID!
    $preArchiveStatus: ApplicationStatus!
  ) {
    updateApplicationStatus(
      applicationID: $applicationID
      status: ARCHIVED
      preArchiveStatus: $preArchiveStatus
    ) {
      applicationID
      status
      updatedAt
    }
  }
`;

// Operator action: restore an ARCHIVED row to whatever status it held
// right before archiving (application.preArchiveStatus, read on the
// client). Same direct-call pattern as archiving; the unarchive flag
// tells the resolver's audit-log pipeline function to write an
// "application_unarchived" row (see updateApplicationStatusAuditLog.js).
export const UNARCHIVE_APPLICATION_MUTATION = /* GraphQL */ `
  mutation UnarchiveApplication(
    $applicationID: ID!
    $status: ApplicationStatus!
  ) {
    updateApplicationStatus(
      applicationID: $applicationID
      status: $status
      unarchive: true
    ) {
      applicationID
      status
      updatedAt
    }
  }
`;

// Operator action: permanently delete an ARCHIVED application, and purge
// its rendered MNSP packet from S3 if it has one. Backed by
// cardConnectStartFunction (see delete_application in
// cardConnectStartHandler.py) rather than a direct DynamoDB resolver,
// since deleting the S3 object requires the Lambda's bucket grant.
// Rejected server-side (status="failed", errorCode=INVALID_STATE) if the
// row isn't ARCHIVED. On success the row is gone, so the caller navigates
// away rather than refetching it.
export const DELETE_APPLICATION_MUTATION = /* GraphQL */ `
  mutation DeleteApplication($applicationID: ID!) {
    deleteApplication(applicationID: $applicationID) {
      status
      errorCode
      message
    }
  }
`;

export const ABANDON_APPLICATION_MUTATION = /* GraphQL */ `
  mutation AbandonApplication($applicationID: ID!, $reason: String!) {
    abandonApplication(applicationID: $applicationID, reason: $reason) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        updatedAt
      }
    }
  }
`;

// ----- Send-form intake (Track 2 send-form workflow) -----------------------

// Operator initiates: create the application row + email the intake link
// to a merchant/tech. Returns the same ApplicationResult envelope as
// createApplication so the form can render upstream errors inline.
export const CREATE_AND_SEND_INTAKE_MUTATION = /* GraphQL */ `
  mutation CreateAndSendIntake(
    $application: CreateApplicationInput!
    $intake: SendIntakeLinkInput!
  ) {
    createAndSendIntake(application: $application, intake: $intake) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        applicationPath
        businessName
        merchantEmail
        intakeRecipientType
        intakeRecipientEmail
        intakeCcEmail
        intakeTokenExpiresAt
        autoSubmit
        createdAt
        updatedAt
      }
    }
  }
`;

// Operator action: rotate token + re-email. Optional intake block to
// swap recipient at the same time.
export const RESEND_INTAKE_LINK_MUTATION = /* GraphQL */ `
  mutation ResendIntakeLink($applicationID: ID!, $intake: SendIntakeLinkInput) {
    resendIntakeLink(applicationID: $applicationID, intake: $intake) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        intakeRecipientType
        intakeRecipientEmail
        intakeTokenExpiresAt
        updatedAt
      }
    }
  }
`;

// Operator kill switch.
export const REVOKE_INTAKE_TOKEN_MUTATION = /* GraphQL */ `
  mutation RevokeIntakeToken($applicationID: ID!) {
    revokeIntakeToken(applicationID: $applicationID) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        intakeRevoked
        updatedAt
      }
    }
  }
`;

// Operator submits a recipient-completed (or partially-completed) row
// to CardConnect. On success, MerchantRaw is REMOVEd from the row and
// the existing CardConnect lifecycle takes over (SIGNATURE_SENT, etc.).
export const SUBMIT_FOR_BOARDING_MUTATION = /* GraphQL */ `
  mutation SubmitForBoarding($applicationID: ID!) {
    submitForBoarding(applicationID: $applicationID) {
      status
      errorCode
      message
      fieldErrors {
        field
        message
      }
      application {
        applicationID
        status
        cardConnectMerchantID
        signatureUrl
        errorDetails
        updatedAt
      }
    }
  }
`;

// Returns the presigned S3 URL for the rendered MAR + EUSA PDF
// tunnelled through the `message` field on success. Resolve as:
//   if (status === "success" && message) window.open(message);
// otherwise surface errorCode + message as a banner.
// Presigned S3 PUT for a merchant document (driver's license / voided check)
// attached on NewApplication. The URL comes back in `message` on success.
export const GENERATE_APPLICATION_DOCUMENT_UPLOAD_URL_MUTATION = /* GraphQL */ `
  mutation GenerateApplicationDocumentUploadUrl(
    $applicationID: ID!
    $kind: String!
    $contentType: String!
    $contentLength: Int!
  ) {
    generateApplicationDocumentUploadUrl(
      applicationID: $applicationID
      kind: $kind
      contentType: $contentType
      contentLength: $contentLength
    ) {
      status
      errorCode
      message
    }
  }
`;

export const GENERATE_MNSP_PACKET_DOWNLOAD_URL_MUTATION = /* GraphQL */ `
  mutation GenerateMnspPacketDownloadUrl($applicationID: ID!) {
    generateMnspPacketDownloadUrl(applicationID: $applicationID) {
      status
      errorCode
      message
    }
  }
`;

// Tamper-detection: re-hashes the stashed MNSP packet in S3 and compares
// to the SHA-256 stored on the row at signing time. Result tunnelled
// through `message`:
//   status=success, message="match"
//   status=success, message="mismatch:<stored>:<recomputed>"
//   status=error,   errorCode in {NO_PDF, NO_STORED_HASH, S3_FETCH_FAILED}
export const VERIFY_MNSP_PDF_HASH_MUTATION = /* GraphQL */ `
  mutation VerifyMnspPdfHash($applicationID: ID!) {
    verifyMnspPdfHash(applicationID: $applicationID) {
      status
      errorCode
      message
    }
  }
`;

export const ON_APPLICATION_STATUS_CHANGED_SUBSCRIPTION = /* GraphQL */ `
  subscription OnApplicationStatusChanged {
    onApplicationStatusChanged {
      applicationID
      status
      cardConnectMerchantID
      frontEndMid
      updatedAt
    }
  }
`;
